import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext)
  // Graceful fallback instead of throwing error
  if (!context) {
    console.warn('useAuth used outside AuthProvider - returning safe defaults')
    return {
      user: null,
      userProfile: null,
      tenantMembership: null,
      currentTenant: null,
      loading: false,
      profileLoading: false,
      notifications: [],
      unreadCount: 0,
      signIn: async () => ({ error: new Error('Auth not initialized') }),
      signUp: async () => ({ error: new Error('Auth not initialized') }),
      signOut: async () => ({ error: new Error('Auth not initialized') }),
      updateProfile: async () => ({ error: new Error('Auth not initialized') }),
      switchTenant: async () => ({ error: new Error('Auth not initialized') }),
      markNotificationRead: async () => {},
      markAllNotificationsRead: async () => {},
      deleteNotification: async () => {}
    }
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [tenantMembership, setTenantMembership] = useState(null)
  const [currentTenant, setCurrentTenant] = useState(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)
  const [notifications, setNotifications] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)

  // Notification operations
  const notificationOperations = {
    async load(userId, tenantId) {
      if (!userId || !tenantId) return
      
      try {
        const { data, error } = await supabase
          ?.from('notifications')
          ?.select('*')
          ?.eq('user_id', userId)
          ?.eq('tenant_id', tenantId)
          ?.order('created_at', { ascending: false })
          ?.limit(50)
        
        if (!error && data) {
          setNotifications(data)
          setUnreadCount(data?.filter(n => !n?.is_read)?.length)
        }
      } catch (error) {
        console.error('Notifications load error:', error)
      }
    },

    setupSubscription(userId, tenantId) {
      if (!userId || !tenantId) return null

      let channel = supabase
        ?.channel('notifications-channel')
        ?.on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${userId}`
          },
          (payload) => {
            if (payload?.eventType === 'INSERT') {
              setNotifications(prev => [payload?.new, ...prev])
              if (!payload?.new?.is_read) {
                setUnreadCount(prev => prev + 1)
              }
            } else if (payload?.eventType === 'UPDATE') {
              setNotifications(prev => 
                prev?.map(n => n?.id === payload?.new?.id ? payload?.new : n)
              )
              // Recalculate unread count
              setNotifications(current => {
                setUnreadCount(current?.filter(n => !n?.is_read)?.length)
                return current
              })
            } else if (payload?.eventType === 'DELETE') {
              setNotifications(prev => prev?.filter(n => n?.id !== payload?.old?.id))
              setUnreadCount(prev => Math.max(0, prev - 1))
            }
          }
        )
        ?.subscribe()

      return channel
    },

    clear() {
      setNotifications([])
      setUnreadCount(0)
    }
  }

  // Isolated async operations - never called from auth callbacks
  const profileOperations = {
    async load(userId) {
      if (!userId) return
      setProfileLoading(true)
      try {
        // Load user profile
        const { data: profile, error: profileError } = await supabase
          ?.from('user_profiles')
          ?.select('*')
          ?.eq('id', userId)
          ?.single()
        
        if (!profileError) setUserProfile(profile)

        // Check if user is super_admin
        const isSuperAdmin = profile?.username === 'omarsterlingjm'

        if (isSuperAdmin) {
          // Super admin doesn't need tenant membership
          setTenantMembership({ role: 'super_admin', is_active: true })
          setCurrentTenant(null) // No specific tenant for super admin
        } else {
          // Load tenant membership for regular users
          const { data: membership, error: membershipError } = await supabase
            ?.from('tenant_members')?.select('*, tenants(*)')?.eq('user_id', userId)?.eq('is_active', true)
            ?.single()
          
          if (!membershipError && membership) {
            setTenantMembership(membership)
            setCurrentTenant(membership?.tenants)
            
            // Load notifications after tenant is set
            notificationOperations?.load(userId, membership?.tenant_id)
          }
        }
      } catch (error) {
        console.error('Profile load error:', error)
      } finally {
        setProfileLoading(false)
      }
    },

    clear() {
      setUserProfile(null)
      setTenantMembership(null)
      setCurrentTenant(null)
      setProfileLoading(false)
      notificationOperations?.clear()
    }
  }

  // Auth state handlers - PROTECTED from async modification
  const authStateHandlers = {
    // This handler MUST remain synchronous - Supabase requirement
    onChange: (event, session) => {
      setUser(session?.user ?? null)
      setLoading(false)
      
      if (session?.user) {
        profileOperations?.load(session?.user?.id) // Fire-and-forget
      } else {
        profileOperations?.clear()
      }
    }
  }

  useEffect(() => {
    // Initial session check
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      authStateHandlers?.onChange(null, session)
    })

    // CRITICAL: This must remain synchronous
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      authStateHandlers?.onChange
    )

    return () => subscription?.unsubscribe()
  }, [])

  // Real-time notifications subscription
  useEffect(() => {
    let channel = null

    if (user?.id && tenantMembership?.tenant_id) {
      channel = notificationOperations?.setupSubscription(
        user?.id,
        tenantMembership?.tenant_id
      )
    }

    return () => {
      if (channel) {
        supabase?.removeChannel(channel)
      }
    }
  }, [user?.id, tenantMembership?.tenant_id])

  // Mark notification as read
  const markNotificationAsRead = async (notificationId) => {
    try {
      const { error } = await supabase
        ?.from('notifications')
        ?.update({ is_read: true, read_at: new Date()?.toISOString() })
        ?.eq('id', notificationId)
      
      if (error) throw error
    } catch (error) {
      console.error('Error marking notification as read:', error)
    }
  }

  // Mark all notifications as read
  const markAllNotificationsAsRead = async () => {
    if (!user?.id || !tenantMembership?.tenant_id) return
    
    try {
      const { error } = await supabase
        ?.from('notifications')
        ?.update({ is_read: true, read_at: new Date()?.toISOString() })
        ?.eq('user_id', user?.id)?.eq('tenant_id', tenantMembership?.tenant_id)?.eq('is_read', false)
      
      if (error) throw error
    } catch (error) {
      console.error('Error marking all notifications as read:', error)
    }
  }

  // Auth methods
  const signUp = async (email, password, fullName, phone, role = 'member') => {
    try {
      // Step 1: Sign up user with Supabase Auth
      const { data: authData, error: authError } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
            phone: phone || '',
            role: role
          }
        }
      })
      
      if (authError) {
        console.error('Auth signup error:', authError)
        return { data: null, error: authError }
      }

      // Step 2: Wait a moment for trigger to create user_profiles
      await new Promise(resolve => setTimeout(resolve, 1000))

      // Step 3: For members, assign to default tenant
      if (role === 'member' && authData?.user) {
        try {
          // Get default tenant
          const { data: tenants, error: tenantError } = await supabase
            ?.from('tenants')?.select('id')?.eq('status', 'active')?.order('created_at', { ascending: true })
            ?.limit(1)
          
          if (!tenantError && tenants && tenants?.length > 0) {
            const defaultTenantId = tenants?.[0]?.id
            
            // Create tenant membership
            const { error: membershipError } = await supabase
              ?.from('tenant_members')
              ?.insert({
                tenant_id: defaultTenantId,
                user_id: authData?.user?.id,
                role: 'member',membership_plan: 'basic',
                is_active: true
              })
            
            if (membershipError) {
              console.error('Membership creation error:', membershipError)
              // Don't fail signup, just log the error
            }
          }
        } catch (memberError) {
          console.error('Error assigning member to tenant:', memberError)
          // Don't fail signup, member can be assigned later
        }
      }

      return { data: authData, error: null }
    } catch (error) {
      console.error('Signup error:', error)
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signIn = async (emailOrUsername, password) => {
    try {
      let email = emailOrUsername
      
      // Check if input is username (not email format)
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(emailOrUsername)) {
        // Lookup email by username
        const { data: profile, error: profileError } = await supabase
          ?.from('user_profiles')?.select('email')?.eq('username', emailOrUsername)
          ?.single()
        
        if (profileError || !profile) {
          return { error: { message: 'Invalid username or password' } }
        }
        
        email = profile?.email
      }
      
      const { data, error } = await supabase?.auth?.signInWithPassword({ email, password })
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signOut = async () => {
    try {
      const { error } = await supabase?.auth?.signOut()
      if (!error) {
        setUser(null)
        profileOperations?.clear()
      }
      return { error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const updateProfile = async (updates) => {
    if (!user) return { error: { message: 'No user logged in' } }
    
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update(updates)?.eq('id', user?.id)?.select()?.single()
      if (!error) setUserProfile(data)
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const createTenant = async (tenantData) => {
    if (!user) return { error: { message: 'No user logged in' } }
    
    try {
      // Create tenant
      const { data: tenant, error: tenantError } = await supabase
        ?.from('tenants')
        ?.insert({
          name: tenantData?.name,
          slug: tenantData?.slug,
          owner_id: user?.id,
          address: tenantData?.address || '',phone: tenantData?.phone || '',email: tenantData?.email || ''
        })
        ?.select()
        ?.single()
      
      if (tenantError) throw tenantError

      // Create tenant membership for owner
      const { data: membership, error: membershipError } = await supabase
        ?.from('tenant_members')
        ?.insert({
          tenant_id: tenant?.id,
          user_id: user?.id,
          role: 'gym_owner',membership_plan: 'elite',
          is_active: true
        })
        ?.select('*, tenants(*)')
        ?.single()
      
      if (membershipError) throw membershipError

      // Update context
      setTenantMembership(membership)
      setCurrentTenant(tenant)

      return { data: tenant, error: null }
    } catch (error) {
      return { error: { message: error?.message || 'Failed to create gym' } };
    }
  }

  const switchTenant = async (tenantId) => {
    if (!user) return { error: { message: 'No user logged in' } }
    
    try {
      const { data: membership, error } = await supabase
        ?.from('tenant_members')
        ?.select('*, tenants(*)')
        ?.eq('tenant_id', tenantId)
        ?.eq('user_id', user?.id)
        ?.eq('is_active', true)
        ?.single()
      
      if (error) throw error

      setTenantMembership(membership)
      setCurrentTenant(membership?.tenants)

      return { data: membership, error: null }
    } catch (error) {
      return { error: { message: 'Failed to switch gym' } }
    }
  }

  const value = {
    user,
    userProfile,
    tenantMembership,
    currentTenant,
    loading,
    profileLoading,
    notifications,
    unreadCount,
    signUp,
    signIn,
    signOut,
    updateProfile,
    createTenant,
    switchTenant,
    markNotificationAsRead,
    markAllNotificationsAsRead,
    isAuthenticated: !!user,
    isSuperAdmin: tenantMembership?.role === 'super_admin',
    isGymOwner: tenantMembership?.role === 'gym_owner',
    isTrainer: tenantMembership?.role === 'trainer',
    isStaff: tenantMembership?.role === 'staff',
    isMember: tenantMembership?.role === 'member'
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}